import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Copy, Users, Crown, Check, Loader2 } from "lucide-react";
import { getApiUrl, getWsUrl } from "../utils/api";

const API = getApiUrl();
const WS_URL = getWsUrl();

export default function Lobby() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ws, setWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const pollingRef = useRef(null);

  const playerId = localStorage.getItem("playerId");

  const fetchRoomState = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rooms/${roomCode}?player_id=${playerId}`);
      setPlayers(response.data.players || []);
      
      const currentPlayer = response.data.players?.find(p => p.id === playerId);
      if (currentPlayer) {
        setIsHost(currentPlayer.is_host);
      }
      
      // If game already started, redirect to game
      if (response.data.phase !== "lobby") {
        navigate(`/game/${roomCode}`);
      }
    } catch (error) {
      toast.error("部屋の情報を取得できませんでした");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [roomCode, playerId, navigate]);

  useEffect(() => {
    fetchRoomState();
  }, [fetchRoomState]);

  // Polling fallback when WebSocket fails
  useEffect(() => {
    // Only start polling if WebSocket is not connected
    if (!wsConnected && !loading) {
      // Clear any existing interval first
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      // Start polling every 3 seconds
      pollingRef.current = setInterval(() => {
        fetchRoomState();
      }, 3000);
      // Also fetch immediately
      fetchRoomState();
    } else if (wsConnected && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [wsConnected, loading, fetchRoomState]);

  useEffect(() => {
    if (!playerId || !roomCode) return;

    const websocket = new WebSocket(`${WS_URL}/ws/${roomCode}/${playerId}`);
    
    websocket.onopen = () => {
      console.log("WebSocket connected");
      setWsConnected(true);
      // Stop polling when WebSocket connects
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    
    websocket.onerror = () => {
      console.log("WebSocket error - falling back to polling");
      setWsConnected(false);
    };

    websocket.onclose = () => {
      setWsConnected(false);
      // Start polling immediately when WebSocket disconnects
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          fetchRoomState();
        }, 3000);
        // Also fetch immediately
        fetchRoomState();
      }
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case "connected":
          // Update with the latest state from server
          if (data.state?.players) {
            setPlayers(data.state.players);
          }
          break;
        case "player_joined":
          // Add player only if not already in the list (prevent duplicates)
          setPlayers(prev => {
            const exists = prev.some(p => p.id === data.player.id);
            if (exists) {
              // Player already exists, update their data instead
              return prev.map(p => p.id === data.player.id ? data.player : p);
            }
            return [...prev, data.player];
          });
          toast.info(`${data.player.nickname} が参加しました`);
          break;
        case "player_left":
          // Remove player from list
          setPlayers(prev => prev.filter(p => p.id !== data.player_id));
          break;
        case "player_disconnected":
          setPlayers(prev => prev.map(p => 
            p.id === data.player_id ? { ...p, is_connected: false } : p
          ));
          break;
        case "player_reconnected":
          setPlayers(prev => prev.map(p => 
            p.id === data.player_id ? { ...p, is_connected: true } : p
          ));
          break;
        case "player_ready":
          setPlayers(prev => prev.map(p =>
            p.id === data.player_id ? { ...p, is_ready: true } : p
          ));
          break;
        case "game_started":
          toast.success("ゲーム開始！");
          navigate(`/game/${roomCode}`);
          break;
        default:
          break;
      }
    };

    setWs(websocket);

    return () => {
      websocket.close();
    };
  }, [roomCode, playerId, navigate, fetchRoomState]);

  const copyRoomCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      toast.success("コードをコピーしました");
    } catch (err) {
      // Fallback: create a temporary input element
      const textArea = document.createElement("textarea");
      textArea.value = roomCode;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("コードをコピーしました");
      } catch (e) {
        toast.info(`参加コード: ${roomCode}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleStartGame = async () => {
    if (players.length !== 4) {
      toast.error("4人のプレイヤーが必要です");
      return;
    }

    setStarting(true);
    try {
      await axios.post(`${API}/rooms/${roomCode}/start?player_id=${playerId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "ゲームを開始できませんでした");
      setStarting(false);
    }
  };

  const handleReady = async () => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/ready?player_id=${playerId}`);
      toast.success("準備完了！");
    } catch (error) {
      toast.error("エラーが発生しました");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brass" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 spotlight" />
      
      <Card className="w-full max-w-lg bg-card/80 backdrop-blur-sm border-brass/30 relative z-10" data-testid="lobby-card">
        <CardHeader className="text-center space-y-4">
          <CardTitle className="font-display text-3xl tracking-wider text-foreground">
            ロビー
          </CardTitle>
          
          {/* Room Code Display */}
          <div 
            onClick={copyRoomCode}
            className="inline-flex items-center gap-3 bg-muted px-6 py-3 cursor-pointer hover:bg-muted/80 transition-colors"
            data-testid="room-code-display"
          >
            <span className="text-xs text-muted-foreground uppercase tracking-widest">参加コード</span>
            <span className="font-data text-2xl tracking-[0.3em] text-brass">{roomCode}</span>
            <Copy className="w-4 h-4 text-muted-foreground" />
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Players List */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>プレイヤー ({players.length}/4)</span>
            </div>
            
            <div className="grid gap-2">
              {[0, 1, 2, 3].map((index) => {
                const player = players[index];
                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 border transition-all ${
                      player 
                        ? "bg-muted/50 border-border" 
                        : "border-dashed border-border/50"
                    }`}
                    data-testid={`player-slot-${index}`}
                  >
                    {player ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${player.is_connected ? "bg-green-500" : "bg-red-500"}`} />
                          <span className="font-medium">{player.nickname}</span>
                          {player.is_host && (
                            <Crown className="w-4 h-4 text-brass" />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {player.is_ready && (
                            <Badge variant="outline" className="text-green-500 border-green-500/30">
                              <Check className="w-3 h-3 mr-1" />
                              準備完了
                            </Badge>
                          )}
                          {player.id === playerId && (
                            <Badge variant="secondary">あなた</Badge>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground text-sm">待機中...</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t border-border">
            {!players.find(p => p.id === playerId)?.is_ready && !isHost && (
              <Button
                data-testid="ready-btn"
                onClick={handleReady}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                準備完了
              </Button>
            )}
            
            {isHost && (
              <Button
                data-testid="start-game-btn"
                onClick={handleStartGame}
                disabled={players.length !== 4 || starting}
                className="w-full bg-brass text-accent-foreground hover:bg-brass/90 font-medium tracking-wide uppercase"
              >
                {starting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    開始中...
                  </>
                ) : players.length !== 4 ? (
                  `あと${4 - players.length}人待ち`
                ) : (
                  "ゲーム開始"
                )}
              </Button>
            )}
          </div>

          {/* Instructions */}
          <div className="text-center text-xs text-muted-foreground">
            <p>参加コードを友達に共有してください</p>
            <p className="mt-1">4人揃うとゲームを開始できます</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
